import React, { useCallback, useState } from "react";
import update from "immutability-helper";
import { usePreview } from "react-dnd-preview";
import DraggableItem from "./DraggableItem";

// Mobile preview workaround: https://www.npmjs.com/package/react-dnd-preview
const DraggableItemMobilePreview = ({ ItemTemplate, items, targetType }) => {
	const preview = usePreview();
	if (!preview.display || targetType !== preview.itemType) {
		return null;
	}

	const { item, style } = preview;
	return (
		<div style={style}>
			<ItemTemplate {...items[item.index]} index={item.index} />
		</div>
	);
};

/**
 * Important: Requires an id field on every item.
 * @param listItems
 * @param ItemTemplate
 * @param targetType
 * @param onChange
 * @returns {JSX.Element}
 * @constructor
 */
const DraggableList = ({
	listItems,
	ItemTemplate,
	targetType,
	onChange = () => {}
}) => {
	const [items, setItems] = useState(listItems.map((i) => i));

	const moveItem = useCallback((dragIndex, hoverIndex) => {
		setItems((prevItems) =>
			update(prevItems, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, prevItems[dragIndex]]
				]
			})
		);
	}, []);

	const dragEnd = useCallback((dropIndex, id) => {
		setItems((prevItems) => {
			const originalIndex = prevItems.findIndex((i) => i.id === id);
			const newOrder = update(prevItems, {
				$splice: [
					[originalIndex, 1],
					[dropIndex, 0, prevItems[originalIndex]]
				]
			});

			onChange(newOrder);

			return newOrder;
		});
	}, []);

	const renderItem = useCallback((item, index) => {
		return (
			<DraggableItem
				targetType={targetType}
				key={item.id}
				id={item.id}
				index={index}
				moveItem={moveItem}
				dragEnd={dragEnd}
			>
				<ItemTemplate {...item} index={index} />
			</DraggableItem>
		);
	}, []);

	return (
		<div>
			<DraggableItemMobilePreview
				ItemTemplate={ItemTemplate}
				items={items}
				targetType={targetType}
			/>
			{items.map((item, i) => renderItem(item, i))}
		</div>
	);
};

export default DraggableList;
